import { useEffect, useRef, useState } from 'react';
import { DocumentDuplicateIcon, PencilSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function focusableNodes(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll('textarea, input, button'))
    .filter((node) => !node.disabled);
}

export function keysDiffer(left, right) {
  const a = left ?? {};
  const b = right ?? {};
  const names = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const name of names) {
    if (String(a[name] ?? '') !== String(b[name] ?? '')) return true;
  }
  return false;
}

/**
 * Architect CMS field: locked text + Edit pencil; unlock to edit with
 * Cancel (X) and Save. Field Save drafts only. No auto-unlock.
 *
 * "Duplicate from…" (editors only, when duplicateCandidates is non-empty)
 * lists the nearest keys that already have prose. Choosing one unlocks the
 * field prefilled with that prose; nothing is written until Save.
 *
 * lockedElsewhere: another field is open, so Edit and Duplicate are disabled
 * (with a title saying why) instead of silently doing nothing. onUnlock may
 * return false to refuse; local duplicate state is only set when accepted.
 */
export default function TemplateKeyField({
  keyName,
  savedValue = '',
  published = '',
  canEdit,
  missing,
  unlocked,
  busy = false,
  justSaved = false,
  onUnlock,
  onCancel,
  onSave,
  duplicateCandidates = [],
  lockedElsewhere = false,
}) {
  const [localValue, setLocalValue] = useState(savedValue ?? '');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [duplicatedFrom, setDuplicatedFrom] = useState(null);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const canDuplicate = canEdit && duplicateCandidates.length > 0;

  // Reset to the saved value whenever the field locks (Cancel, Save, reload).
  // While unlocked, keep whatever the editor holds, including a duplicate.
  useEffect(() => {
    if (!unlocked) {
      setLocalValue(savedValue ?? '');
      setDuplicatedFrom(null);
    }
  }, [savedValue, unlocked]);

  const blockedTitle = lockedElsewhere ? 'Save or cancel the open field first' : null;

  useEffect(() => {
    if (unlocked) inputRef.current?.focus();
  }, [unlocked]);

  function handleCancel() {
    setLocalValue(savedValue ?? '');
    setDuplicatedFrom(null);
    onCancel?.();
  }

  function requestUnlock() {
    if (lockedElsewhere) return false;
    return onUnlock?.() !== false;
  }

  function handleUnlock() {
    if (!requestUnlock()) return;
    setLocalValue(savedValue ?? '');
    setDuplicatedFrom(null);
  }

  function handleDuplicate(candidate) {
    if (!requestUnlock()) return;
    setLocalValue(candidate.prose);
    setDuplicatedFrom(candidate.key);
    setPickerOpen(false);
  }

  function handleSave() {
    onSave?.(localValue);
  }

  function handleKeyDown(event) {
    if (!unlocked) {
      if (pickerOpen && event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setPickerOpen(false);
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      handleCancel();
      return;
    }
    if (event.key === 'Enter' && event.target.tagName === 'INPUT') {
      event.preventDefault();
      handleSave();
      return;
    }
    if (event.key === 'Tab') {
      const nodes = focusableNodes(rootRef.current);
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const display = canEdit ? (savedValue || '') : (published || '');

  return (
    <div
      className="relative rounded-md border border-gray-200 p-3 pr-12"
      data-template-key={keyName}
      onKeyDown={handleKeyDown}
      ref={rootRef}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2 pr-16">
        <code className="text-xs font-semibold text-gray-800">{keyName}</code>
        <span className={classNames(
          'rounded-full px-2 py-0.5 text-xs font-medium',
          missing ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
        )}
        >
          {missing ? 'Missing' : 'Filled'}
        </span>
        {justSaved && (
          <span className="text-xs text-gray-500" role="status">Saved</span>
        )}
        {canDuplicate && !unlocked && (
          <button
            aria-expanded={pickerOpen}
            aria-label={`Duplicate from… into ${keyName}`}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 hover:text-indigo-800 disabled:opacity-40"
            disabled={busy || lockedElsewhere}
            onClick={() => setPickerOpen((open) => !open)}
            title={blockedTitle ?? 'Start from the closest existing template'}
            type="button"
          >
            <DocumentDuplicateIcon aria-hidden="true" className="h-3.5 w-3.5" />
            Duplicate from…
          </button>
        )}
      </div>

      {canDuplicate && pickerOpen && !unlocked && !lockedElsewhere && (
        <div
          aria-label={`Closest templates to ${keyName}`}
          className="mb-3 space-y-2 rounded-md border border-indigo-100 bg-indigo-50/40 p-2"
          role="group"
        >
          <p className="text-xs text-gray-600">
            Closest keys with prose (fewest code differences first). Using one opens the editor
            prefilled; nothing is saved until you click Save, and Save writes the draft only.
          </p>
          <ul className="space-y-2">
            {duplicateCandidates.map((candidate) => (
              <li className="rounded-md border border-gray-200 bg-white p-2" key={candidate.key}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="text-xs font-semibold text-gray-800">{candidate.key}</code>
                    {candidate.differing?.length > 0 && (
                      <span className="text-xs text-gray-500">
                        differs in {candidate.differing.join(', ')}
                      </span>
                    )}
                  </div>
                  <button
                    aria-label={`Duplicate ${candidate.key} into ${keyName}`}
                    className="rounded-md bg-white px-2 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50"
                    disabled={busy || lockedElsewhere}
                    onClick={() => handleDuplicate(candidate)}
                    type="button"
                  >
                    Use this
                  </button>
                </div>
                <p className="mt-1 max-h-24 overflow-auto whitespace-pre-wrap text-xs text-gray-700">
                  {candidate.prose}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {canEdit && (
        <div className="absolute right-2 top-2 flex items-center gap-1">
          {unlocked ? (
            <>
              <button
                aria-label={`Cancel ${keyName}`}
                className="rounded-md p-1 text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                disabled={busy}
                onClick={handleCancel}
                title="Cancel"
                type="button"
              >
                <XMarkIcon aria-hidden="true" className="h-4 w-4" />
              </button>
              <button
                aria-label={`Save ${keyName}`}
                className="rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white hover:bg-indigo-500 disabled:bg-indigo-300"
                disabled={busy}
                onClick={handleSave}
                type="button"
              >
                Save
              </button>
            </>
          ) : (
            <button
              aria-label={`Edit ${keyName}`}
              className="rounded-md p-1 text-gray-500 hover:bg-gray-50 hover:text-indigo-600 disabled:opacity-40"
              disabled={busy || lockedElsewhere}
              onClick={handleUnlock}
              title={blockedTitle ?? 'Edit'}
              type="button"
            >
              <PencilSquareIcon aria-hidden="true" className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {unlocked && canEdit && duplicatedFrom && (
        <p className="mb-2 text-xs text-indigo-700" role="note">
          Duplicated from <code>{duplicatedFrom}</code>. Edit the wording for {keyName}, then Save to the draft.
        </p>
      )}

      {unlocked && canEdit ? (
        <textarea
          aria-label={`Prose for ${keyName}`}
          className="w-full rounded-md border border-gray-300 bg-white p-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          onChange={(event) => setLocalValue(event.target.value)}
          onDoubleClick={(event) => event.stopPropagation()}
          placeholder="Keyed prose. Use [] for the client name. Do not invent clinical wording."
          ref={inputRef}
          rows={4}
          value={localValue}
        />
      ) : (
        <p
          className={classNames(
            'whitespace-pre-wrap text-sm',
            display ? 'text-gray-800' : 'text-gray-400'
          )}
          onDoubleClick={(event) => event.preventDefault()}
        >
          {display || (canEdit ? 'No prose for this key.' : 'No published prose for this key.')}
        </p>
      )}
    </div>
  );
}
